import type { DatabaseFile } from '../../types';
import { withoutGitBusyOverlay } from '../../utils/gitBusy';
import { folderLabel, parentDirOfMarkdown } from '../../utils/markdownPaths';
import { appAlert, appChoose, appConfirm } from '../../utils/appDialog';
import { sortPickerByLabel } from '../../utils/sortPickerChoices';
import { generateAndCopyShareLink } from '../../utils/shareLinkFlow';
import { sharePlaintextForPage } from '../../utils/sharePayload';
import { openPageIconPicker } from '../../utils/pageIcon';
import { openItemTextEditor } from '../../utils/itemTextModal';
import type { FileViewDeps } from './FileViewDeps';
import type { FileViewSections } from './FileViewSections';

export class FileViewChrome {
  constructor(
    private readonly deps: FileViewDeps,
    private readonly sections: FileViewSections
  ) {}

  private get onSave() {
    return this.deps.onSave;
  }
  private get onRename() {
    return this.deps.onRename;
  }
  private get onDelete() {
    return this.deps.onDelete;
  }
  private get onDiscard() {
    return this.deps.onDiscard;
  }
  private get listFolders() {
    return this.deps.listFolders;
  }
  private get onMoveFileToFolder() {
    return this.deps.onMoveFileToFolder;
  }
  private get getPageIcon() {
    return this.deps.getPageIcon;
  }
  private get onSetPageIcon() {
    return this.deps.onSetPageIcon;
  }

  wireSharePage(container: HTMLElement, file: DatabaseFile): void {
    const btn = container.querySelector<HTMLButtonElement>('#btn-share-page');
    if (!btn) return;
    btn.addEventListener('click', () => {
      void generateAndCopyShareLink(sharePlaintextForPage(file));
    });
  }

  wirePageIcon(container: HTMLElement, file: DatabaseFile): void {
    const btn = container.querySelector<HTMLButtonElement>('#page-icon-btn');
    if (!btn || !this.onSetPageIcon) return;
    btn.addEventListener('click', () => {
      void (async () => {
        const current = this.getPageIcon?.(file.path) ?? '📄';
        const picked = await openPageIconPicker(current);
        if (picked === null) return;
        await this.onSetPageIcon!(file.path, picked);
      })();
    });
  }

  wireAddSection(container: HTMLElement, file: DatabaseFile, onDirty: () => void): void {
    const btn = container.querySelector<HTMLButtonElement>('#add-section-btn')!;

    btn.addEventListener('click', () => {
      openItemTextEditor({
        value: '',
        title: 'New section',
        placeholder: 'Section name',
        singleLine: true,
        onDone: title => {
          if (!title) return;
          file.sections.push({ title, level: 2, items: [], prose: [] });
          this.sections.renderSections(container, file, onDirty);
          onDirty();
        },
      });
    });
  }

  wireMoveFile(container: HTMLElement, file: DatabaseFile): void {
    const btn = container.querySelector<HTMLButtonElement>('#move-file-btn')!;
    btn.addEventListener('click', async () => {
      const folders = this.listFolders!();
      const current = parentDirOfMarkdown(file.path);
      const target = await appChoose({
        title: `Move "${file.name}"`,
        message: 'Choose a folder for this page.',
        choices: sortPickerByLabel(
          folders.map(f => {
            const choice: { value: string; label: string; hint?: string } = {
              value: f,
              label: folderLabel(f),
            };
            if (f === current) choice.hint = 'Current location';
            return choice;
          })
        ),
      });
      if (!target || target === current) return;

      btn.disabled = true;
      try {
        await this.onMoveFileToFolder!(file, target);
      } catch (err) {
        await appAlert(`Move failed: ${(err as Error).message}`, 'Move failed');
      } finally {
        btn.disabled = false;
      }
    });
  }

  wireRename(container: HTMLElement, file: DatabaseFile): void {
    const renameBtn = container.querySelector<HTMLButtonElement>('#rename-btn')!;
    const titleDisplay = container.querySelector<HTMLElement>('#file-title-display')!;

    renameBtn.addEventListener('click', () => {
      openItemTextEditor({
        value: file.name,
        title: 'Rename file',
        message: 'Renames the file in the repo.',
        placeholder: 'New name',
        singleLine: true,
        onDone: newName => {
          if (!newName || newName === file.name) return;
          void (async () => {
            try {
              await this.onRename(file, newName);
              titleDisplay.textContent = newName;
            } catch (err) {
              await appAlert(`Rename failed: ${(err as Error).message}`, 'Rename failed');
            }
          })();
        },
      });
    });
  }

  wireDeleteFile(container: HTMLElement, file: DatabaseFile): void {
    const btn = container.querySelector<HTMLButtonElement>('#delete-file-btn');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const confirmed = await appConfirm(
        'This will permanently remove the file from the repo. This cannot be undone.',
        {
          title: `Delete "${file.name}"?`,
          confirmLabel: 'Delete',
          danger: true,
        }
      );
      if (!confirmed) return;
      btn.disabled = true;
      btn.innerHTML = '<span class="app-spinner app-spinner--sm" aria-hidden="true"></span>';
      btn.setAttribute('aria-busy', 'true');
      try {
        await withoutGitBusyOverlay(() => this.onDelete(file));
        container.innerHTML = `
          <div class="empty-state">
            <div class="empty-icon">🗑</div>
            <p>"${file.name}" was deleted.</p>
          </div>`;
      } catch (err) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = '🗑';
        await appAlert(`Delete failed: ${(err as Error).message}`, 'Delete failed');
      }
    });
  }
}
